import dayjs from 'dayjs';
import {
  ServiceOriginal,
  Services,
} from '../../../../store/slices/timesheet/timesheetPageSlice';
import { WorkTimePeriod } from './WorkTimes/WorkPeriod';

/**
 * Returns the validation field name based on type and field type.
 *
 * @param type - Type of change ('start' or 'end')
 * @param fieldType - Type of field ('time' or 'date')
 * @returns Name of the validation field
 */
export const getIsValidField = (
  type: 'start' | 'end',
  fieldType: 'time' | 'date',
): string => {
  if (fieldType === 'time') {
    return type === 'start' ? 'isStartTimeValid' : 'isEndTimeValid';
  }
  return type === 'start' ? 'isStartDateValid' : 'isEndDateValid';
};

/**
 * Validates if the service has valid start and end date-times.
 *
 * @param service - The service object to validate
 * @returns True if all date and time fields are valid, false otherwise
 */
export const validateTime = (service: ServiceOriginal): boolean => {
  if (!service || !service.startDateTime || !service.endDateTime) {
    return false;
  }
  const startTime = dayjs(service?.startDateTime);
  const endTime = dayjs(service?.endDateTime);

  return startTime.isValid() && endTime.isValid();
};

/**
 * Sums total minutes between date pairs.
 *
 * @param periods - Array of objects with start and end as dayjs instances or null
 * @returns Total minutes between all valid period pairs
 */
export function sumPeriodsMinutes(
  periods: Array<{ start: dayjs.Dayjs | null; end: dayjs.Dayjs | null }>,
) {
  return periods.reduce((total, { start, end }) => {
    if (start?.isValid() && end?.isValid()) {
      return total + end.diff(start, 'minute');
    }
    return total;
  }, 0);
}

/**
 * Extracts valid periods from workTimes.
 *
 * @param workTimes - Array of WorkTimePeriod objects
 * @returns Array of objects with start and end as dayjs instances or null
 */
export function getWorkTimePeriods(workTimes: WorkTimePeriod[]) {
  // Build combined datetimes by applying the time fields onto the date object.
  // Starting from dayjs() and then chaining .date()/.month()/.year() is unsafe:
  // if "today" has a higher day-of-month than the target month supports (e.g. today
  // is June 31 does not exist so it overflows to July 1), the subsequent .month()
  // call lands on the wrong day. Using the existing date object as the base avoids
  // any day-overflow entirely.
  return workTimes.map((wt) => ({
    start:
      wt.startTime && wt.startDate
        ? wt.startDate
            .hour(wt.startTime.hour())
            .minute(wt.startTime.minute())
            .second(wt.startTime.second())
        : null,
    end:
      wt.endTime && wt.endDate
        ? wt.endDate
            .hour(wt.endTime.hour())
            .minute(wt.endTime.minute())
            .second(wt.endTime.second())
        : null,
  }));
}

/**
 * Extracts valid periods from services.
 *
 * @param services - Array of Services objects
 * @param validateTime - Function to validate a service
 * @returns Array of objects with start and end as dayjs instances or null
 */
export function getServicePeriods(
  services: Services[],
  validateTime: (s: any) => boolean,
) {
  if (services.length === 0) return [];

  return services.map((workTime) => {
    if (!validateTime(workTime.service)) return { start: null, end: null };
    const startTime = dayjs(workTime?.service?.startDateTime);
    const endTime = dayjs(workTime?.service?.endDateTime);
    return { start: startTime, end: endTime };
  });
}

/**
 * Formats total minutes to "HH:mm" format.
 *
 * @param totalMinutes - Total minutes to format
 * @returns String in HH:mm format
 */
export function formatMinutesToHHmm(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}
